import { datadogRum } from '@datadog/browser-rum';
import { useAuth } from '@state/auth';
import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { buildV2RedirectUrl } from '@/lib/buildV2RedirectUrl';
import { isManagementPath } from '@/lib/postLoginPath';
import { setOptedInToV2 } from '@/lib/v2OptInState';
import pkg from '../../package.json';

const FRONTEND_V2_URL = import.meta.env.VITE_FRONTEND_V2_URL;
const ENC_KEY = import.meta.env.VITE_REDIRECT_ENCRYPTION_KEY;
const RETRACED_ENV = import.meta.env.VITE_RETRACED_ENV;

/** The RUM SDK sends actions in the background, and `location.assign()` tears
 *  the document down — without a beat to flush, the opt-in is lost and the
 *  adoption numbers under-count. */
const RUM_FLUSH_MS = 150;

/**
 * Sends the user to frontend-v2, from wherever we offer it.
 *
 * Shared by the navbar banner and the launch dialog so a single definition owns
 * the encrypted-token URL, the `frontend_v2_try_now_clicked` action every
 * adoption metric is built on, and the opt-in flag. Two copies of this drifted
 * apart the moment either one changed.
 */
export function useOpenFrontendV2(source: string) {
  const { pathname } = useLocation();
  const me = useAuth(state => state.me);
  const impersonateId = useAuth(state => state.impersonateId);
  const accessToken = useAuth(state => state.accessToken);
  const refreshToken = useAuth(state => state.refreshToken);
  const sessionToken = useAuth(state => state.sessionToken);
  const companyId = useAuth(state => state.companyId);
  const [opening, setOpening] = useState(false);

  async function open() {
    if (opening) return;
    setOpening(true);
    try {
      const url = await buildV2RedirectUrl({
        baseUrl: FRONTEND_V2_URL,
        accessToken,
        refreshToken,
        sessionToken,
        impersonateId,
        companyId: companyId ?? me?.company?.id,
        encKey: ENC_KEY,
      });
      setOptedInToV2();
      // Client pages only. These actions measure customer adoption of the new
      // dashboard; staff moving between two internal tool sets is not that, and
      // counting it would inflate every number built on this action.
      const tracked = !isManagementPath(pathname);
      if (tracked) {
        datadogRum.addAction('frontend_v2_try_now_clicked', {
          Version: pkg.version,
          env: RETRACED_ENV,
          userId: me?.id,
          companyId: companyId ?? me?.company?.id,
          impersonating: !!impersonateId,
          pathname,
          // Which surface sent them — the banner or the launch dialog.
          source,
        });
        // The flush wait only earns its stall when there is an action to flush.
        await new Promise(resolve => setTimeout(resolve, RUM_FLUSH_MS));
      }
      window.location.assign(url);
    } finally {
      setOpening(false);
    }
  }

  return { open, opening };
}
